import { CATALOG, MAKES } from "../data/catalog";

const norm = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
const YEARS_RE = /\b((?:19|20)\d{2})[\s\-–_]*(?:to)?[\s\-–_]*((?:19|20)\d{2})\b|\b((?:19|20)\d{2})\s*[-–]\s*(\d{2})(?!\d)|\b((?:19|20)\d{2})\b/;
const ALIASES = { chevy: "Chevrolet", mercedes: "Mercedes-Benz", benz: "Mercedes-Benz", vw: "Volkswagen", landrover: "Land Rover", rangerover: "Land Rover", alfa: "Alfa Romeo", "mini cooper": "MINI", infinity: "Infiniti", hyundia: "Hyundai" };

export function parseVehicleName(raw) {
  let name = raw.replace(/\.[a-z0-9]+$/i, "").replace(/[_]+/g, " ").trim();
  let year_start = "", year_end = "";
  const m = YEARS_RE.exec(name);
  if (m) {
    if (m[1]) { year_start = m[1]; year_end = m[2]; }
    else if (m[3]) { year_start = m[3]; year_end = m[3].slice(0, 2) + m[4]; }
    else { year_start = year_end = m[5]; }
    name = (name.slice(0, m.index) + " " + name.slice(m.index + m[0].length)).replace(/\s+/g, " ").trim();
  }
  const n = norm(name);
  let make = "", model = "";
  for (const mk of [...MAKES].sort((a, b) => b.length - a.length)) {
    const nm = norm(mk);
    if (n === nm || n.startsWith(nm + " ")) { make = mk; model = name.slice(name.toLowerCase().indexOf(mk.toLowerCase()) + mk.length).trim(); break; }
  }
  if (!make) {
    const first = name.split(" ")[0];
    const alias = Object.keys(ALIASES).find((a) => n === a || n.startsWith(a + " ") || norm(first) === a.replace(/ /g, ""));
    make = alias ? ALIASES[alias] : MAKES.find((mk) => norm(mk) === norm(first)) || "";
    if (alias && /^rangerover$/.test(alias)) model = name; else model = make ? name.slice(alias ? alias.length : first.length).trim() : name;
  }
  if (make && model) {
    const cands = [...(CATALOG[make] || [])].sort((a, b) => b.length - a.length);
    const hit = cands.find((x) => norm(x) === norm(model)) || cands.find((x) => norm(model).startsWith(norm(x) + " ")) || cands.find((x) => norm(model).startsWith(norm(x)));
    if (hit) model = hit;
  }
  return { make, model: model.replace(/[-–\s]+$/, ""), year_start, year_end };
}

export function groupFiles(files) {
  const groups = new Map();
  for (const file of files) {
    if (!/\.(pdf|svg|zip|ai|eps|dxf|plt|hpgl|hgl)$/i.test(file.name)) continue;
    const rel = file.webkitRelativePath || file.name;
    const parts = rel.split("/").filter(Boolean);
    let parsed = parseVehicleName(file.name);
    let source = file.name;
    if (!parsed.year_start && parts.length > 1) { parsed = parseVehicleName(parts[parts.length - 2]); source = parts[parts.length - 2]; }
    const key = `${parsed.make}|${parsed.model}|${parsed.year_start}|${parsed.year_end}|${source}`;
    if (!groups.has(key)) groups.set(key, { id: key, ...parsed, source, files: [], body_style: "Sedan" });
    groups.get(key).files.push(file);
  }
  return [...groups.values()];
}

export const groupValid = (g) => Boolean(g.make && g.model && g.year_start && g.year_end && g.files.length);

const BODY_WORDS = [["track loader", "Track Loader"], ["skid steer", "Skid Steer"], ["wheel loader", "Wheel Loader"], ["mini excavator", "Mini Excavator"], ["excavator", "Excavator"], ["backhoe", "Backhoe"], ["tractor", "Tractor"], ["lwb", "LWB Sedan"], ["long wheelbase", "LWB Sedan"], ["swb", "SWB Sedan"], ["short wheelbase", "SWB Sedan"], ["suv", "SUV"], ["crossover", "Crossover"], ["sedan", "Sedan"], ["coupe", "Coupe"], ["hatch", "Hatchback"], ["wagon", "Wagon"], ["sportwagon", "4D Sportwagon"], ["convertible", "Convertible"], ["cabrio", "Convertible"], ["van", "Van"], ["crew cab", "Crew Cab"], ["crewcab", "Crew Cab"], ["extended cab", "Extended Cab"], ["ext cab", "Extended Cab"], ["regular cab", "Regular Cab"], ["reg cab", "Regular Cab"], ["truck", "Crew Cab"], ["pickup", "Crew Cab"]];
const MODEL_INDEX = Object.entries(CATALOG).flatMap(([mk, models]) => models.filter((m) => norm(m).length >= 3).map((m) => ({ make: mk, model: m, key: norm(m) }))).sort((a, b) => b.key.length - a.key.length);

export function detectVehicle(files, texts = []) {
  const cands = [];
  for (const f of files) {
    const parts = (f.webkitRelativePath || f.name).split("/").filter(Boolean);
    cands.push(f.name, ...parts.slice(0, -1).reverse());
  }
  cands.push(...texts);
  const out = { make: "", model: "", year_start: "", year_end: "", body_style: "", detected: false };
  for (const c of cands) {
    const p = parseVehicleName(c);
    if (p.make && p.model && (CATALOG[p.make] || []).includes(p.model)) { Object.assign(out, { make: p.make, model: p.model, detected: true }); break; }
  }
  if (!out.make) {
    for (const c of cands) {
      const n = ` ${norm(c)} `;
      const hit = MODEL_INDEX.find((m) => n.includes(` ${m.key} `));
      if (hit) { Object.assign(out, { make: hit.make, model: hit.model, detected: true }); break; }
    }
  }
  const yearCands = cands.filter((c) => !/\b(?:19|20)\d{2}-\d{2}-\d{2}\b|^(?:screenshot|img_|image)/i.test(c.trim()));  // "Screenshot 2026-10-13 012731.png" is a date, not a model year
  for (const c of yearCands) {
    const p = parseVehicleName(c);
    if (p.year_start) { out.year_start = p.year_start; out.year_end = p.year_end || p.year_start; break; }
  }
  for (const c of cands) {
    const n = norm(c);
    const hit = BODY_WORDS.find(([w]) => n.includes(w));
    if (hit) { out.body_style = hit[1]; break; }
  }
  return out;
}

// DigiCut names the vehicle in full ("2027 Audi A6 Sportback e-tron Premium/Premium Plus/Pr…", often cut off) while the built-in catalog
// only knows "A6". When the owner already has a longer model for that make in the library, the kit must land there — not in a new "A6".
export function bestLibraryModel(make, texts, libraryModels, detected = "") {
  if (!make || !texts?.length || !libraryModels?.length) return null;
  const nm = norm(make);
  const tails = texts.map(norm).filter((t) => t.includes(nm)).map((t) => t.slice(t.indexOf(nm) + nm.length).trim());
  if (!tails.length) return null;
  const prefixLen = (a, b) => { let i = 0; while (i < a.length && i < b.length && a[i] === b[i]) i++; return i; };
  let best = null, bestScore = detected ? Math.max(...tails.map((t) => prefixLen(norm(detected), t))) : 0;
  for (const m of libraryModels) {
    const k = norm(m);
    const score = Math.max(...tails.map((t) => prefixLen(k, t)));
    if (score >= Math.min(k.length, 8) && score > bestScore) { best = m; bestScore = score; }
  }
  return best;
}
